<!--
  ProductReadinessFlag.vue (Phase 2.2 -- card https://trello.com/c/hYlwyKYT).

  Per-product readiness badge that renders next to a product card in
  the store products list (ADR 0041). Surfaces the approval state of
  the product's decorations at a glance and links into the Approval
  Workspace filtered by product.

  Accessibility: state is encoded via COLOR + SHAPE (the icon shape +
  fill differ per state) so the badge is meaningful even with color-
  vision deficiency (P2-Q5).

  Labelling (Jake, 2026-08): every label is prefixed "Deco:" (TEXT, not
  an icon -- not all decorations are embroidery, so a needle icon isn't
  universal) so the bubble is obviously about decoration state. The
  approval-vocabulary state 'awaiting' DISPLAYS as "pending" ("awaiting"
  is meaningless to builders); the backend wire key stays 'awaiting'.

  State 'unapproved' (placed but never submitted for approval) is
  distinct from 'none' (no decorations at all) and is a CTA -- clicking
  it navigates the builder to the approval page to act.

  Props:
    - state: 'rejected' | 'changes_requested' | 'awaiting' |
             'approved' | 'unapproved' | 'none'
    - count: number -- decorations in the most-concerning state
    - decorationCount: number -- total decorations on the product (for
      the "no decoration configured" subtitle when state === 'none')
    - campaignProductId: number -- backref so the click handler can
      apply the workspace filter

  Emits:
    - click(payload) -- payload = { campaignProductId, state, count,
      isActionable }. Parent navigates for actionable states (the CTA);
      approved/none are non-actionable (no-op or view).
-->
<template>
  <button
    type="button"
    class="prf"
    :class="`prf--${state}`"
    :data-testid="`product-readiness-flag-${campaignProductId}`"
    :aria-label="ariaLabel"
    :title="tooltipText"
    @click="onClick"
  >
    <span class="prf__icon" aria-hidden="true">
      <span :class="`prf__shape prf__shape--${state}`"></span>
    </span>
    <span class="prf__label" data-testid="product-readiness-flag-label">
      {{ shortLabel }}
    </span>
  </button>
</template>

<script>
// Static maps; the JS-readable shape vocabulary is intentionally not
// derived from CSS so unit tests can assert on the resulting wire-
// independent shape name.

// Every label is prefixed "Deco:" so the bubble reads as a decoration-
// state marker (Jake: text prefix, not an icon -- decorations aren't
// all embroidery). Labels are SHORT so they fit the card corner.
// 'awaiting' displays as "pending"; the count-bearing states embed N.
const VALID_STATES = [
  "rejected",
  "changes_requested",
  "awaiting",
  "approved",
  "unapproved",
  "none",
];

// States the builder can ACT on -- clicking navigates to the approval
// page. approved/none are non-actionable (terminal / nothing to do).
const ACTIONABLE_STATES = [
  "rejected",
  "changes_requested",
  "awaiting",
  "unapproved",
];

const SHORT_LABELS = {
  rejected: () => "Deco: rejected",
  changes_requested: () => "Deco: needs changes",
  // 'awaiting' displays as "pending".
  awaiting: ({ count }) => `Deco: ${count} pending`,
  approved: () => "Deco: approved",
  unapproved: ({ count }) => `Deco: ${count} unapproved`,
  none: () => "Deco: none",
};

const TOOLTIP_TEMPLATES = {
  rejected: ({ count }) =>
    count === 1
      ? "1 decoration was rejected"
      : `${count} decorations were rejected`,
  changes_requested: ({ count }) =>
    count === 1
      ? "1 decoration needs changes"
      : `${count} decorations need changes`,
  awaiting: ({ count }) =>
    count === 1
      ? "1 decoration pending approval"
      : `${count} decorations pending approval`,
  approved: () => "All decorations approved",
  unapproved: ({ count }) =>
    count === 1
      ? "1 decoration not yet submitted for approval"
      : `${count} decorations not yet submitted for approval`,
  none: () => "No decorations placed",
};

export default {
  name: "ProductReadinessFlag",
  props: {
    state: {
      type: String,
      required: true,
      validator: (v) => VALID_STATES.includes(v),
    },
    count: { type: Number, default: 0 },
    decorationCount: { type: Number, default: 0 },
    campaignProductId: { type: [Number, String], required: true },
  },
  emits: ["click"],
  computed: {
    shortLabel() {
      const tmpl = SHORT_LABELS[this.state] || (() => this.state);
      return tmpl({ count: this.count });
    },
    tooltipText() {
      const tmpl = TOOLTIP_TEMPLATES[this.state] || (() => this.state);
      return tmpl({ count: this.count });
    },
    ariaLabel() {
      return `${this.shortLabel} -- ${this.tooltipText}`;
    },
    isActionable() {
      return ACTIONABLE_STATES.includes(this.state);
    },
  },
  methods: {
    onClick() {
      this.$emit("click", {
        campaignProductId: this.campaignProductId,
        state: this.state,
        count: this.count,
        isActionable: this.isActionable,
      });
    },
  },
};
</script>

<style scoped>
.prf {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border: 1px solid transparent;
  border-radius: 999px;
  background: transparent;
  cursor: pointer;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.01em;
  transition: filter 0.12s ease;
}

.prf:hover {
  filter: brightness(0.96);
}

/* State variants: COLOR + SHAPE both encode state. The shape lives in
 * .prf__shape; the color lives on the chip border + label fill. The
 * .prf__shape rules below set both ``border-radius`` and ``transform``
 * so each state has a distinct silhouette (circle / square /
 * triangle / diamond / dash) -- accessible to color-blind users.
 */
.prf__icon {
  width: 14px;
  height: 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.prf__shape {
  width: 12px;
  height: 12px;
  display: inline-block;
}

/* Approved: green circle */
.prf--approved {
  background: rgba(0, 184, 92, 0.12);
  color: #0a7c3e;
  border-color: rgba(0, 184, 92, 0.4);
}
.prf__shape--approved {
  background: #00b85c;
  border-radius: 50%;
}

/* Awaiting: amber rounded square */
.prf--awaiting {
  background: rgba(255, 168, 0, 0.14);
  color: #8a5300;
  border-color: rgba(255, 168, 0, 0.5);
}
.prf__shape--awaiting {
  background: #ffa800;
  border-radius: 3px;
}

/* Changes requested: red triangle */
.prf--changes_requested {
  background: rgba(225, 50, 50, 0.12);
  color: #9c1b1b;
  border-color: rgba(225, 50, 50, 0.4);
}
.prf__shape--changes_requested {
  width: 0;
  height: 0;
  border-left: 7px solid transparent;
  border-right: 7px solid transparent;
  border-bottom: 12px solid #e13232;
  background: transparent;
  border-radius: 0;
}

/* Rejected: dark red diamond */
.prf--rejected {
  background: rgba(150, 26, 26, 0.12);
  color: #6a0e0e;
  border-color: rgba(150, 26, 26, 0.45);
}
.prf__shape--rejected {
  background: #961a1a;
  transform: rotate(45deg);
  border-radius: 2px;
}

/* Unapproved: blue/neutral hollow ring (placed, pre-approval -- a CTA).
 * The ring silhouette (outline, no fill) is distinct from approved's
 * SOLID green circle so the two read apart without color (P2-Q5). */
.prf--unapproved {
  background: rgba(37, 99, 235, 0.1);
  color: #1d4ed8;
  border-color: rgba(37, 99, 235, 0.4);
}
.prf__shape--unapproved {
  background: transparent;
  border: 2.5px solid #2563eb;
  border-radius: 50%;
}

/* None: gray dash */
.prf--none {
  background: transparent;
  color: #6b7280;
  border-color: #d1d5db;
}
.prf__shape--none {
  background: #9ca3af;
  width: 12px;
  height: 2px;
  margin-top: 5px;
  border-radius: 1px;
}
</style>
